/**
 * @file src/shell/ExercisePanel.js
 * Panel "Bài tập" — TEMPLATE trắc nghiệm A/B/C/D DÙNG CHUNG cho mọi tiết, mở từ nút
 * "Bài tập" ở Header.
 *
 * File này KHÔNG biết câu hỏi lấy từ đâu — nó chỉ hiển thị bất kỳ danh sách câu hỏi
 * nào được cấp qua `ctx.ui.setQuiz({ fetchQuestions })` (xem UiApi trong Shell.js).
 * `fetchQuestions()` trả về Promise<QuizQuestion[]> — tiết tự quyết nguồn dữ liệu:
 *   · lesson.js đọc CFG.quiz (dữ liệu tĩnh trong config.js, dịch qua ctx.t()) rồi bọc
 *     vào một hàm async — xem lesson.js của tiết.
 *   · SAU NÀY muốn đổi sang gọi API thật: chỉ cần sửa lại THÂN hàm fetchQuestions đó
 *     (ví dụ `fetch('https://.../quiz').then(r => r.json())`) — file này và hợp đồng
 *     ctx.ui.setQuiz() không cần đổi gì cả. Đúng như yêu cầu: "tạo sẵn 1 template bài
 *     tập, config.js chỉ việc chỏ vào đó để hiển thị".
 *
 * @typedef {Object} QuizQuestion
 * @property {string} section    Nhãn nhóm, hiện ở "Câu X/Y · <section>"
 * @property {string} question
 * @property {string[]} options  Văn bản đã dịch — panel tự sinh nhãn A/B/C/D theo thứ tự
 * @property {number} correctIndex
 *
 * Cùng slot với InfoPanel/SettingsPanel (mount vào panelHost, mép phải sân khấu) —
 * BA panel này dùng chung một chỗ nên chỉ mở được MỘT, xem cụm bInfo/bSettings/
 * bExercise ở Shell.js.
 */

import { icon } from './icons.js';

/**
 * @typedef {Object} QuizSource
 * @property {() => Promise<QuizQuestion[]>} fetchQuestions
 */

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {(k: string, v?: Record<string, string|number>) => string} o.t
 * @param {(open: boolean) => void} [o.onOpenChange]
 */
export function createExercisePanel(o) {
  const el = document.createElement('aside');
  el.className = 'ol-exercise';
  el.innerHTML = `
    <div class="ol-exercise__head">
      <span class="ol-icon">${icon('notebookSquare')}</span>
      <span class="ol-exercise__title"></span>
      <button class="ol-btn ol-btn--icon ol-exercise__close"><span class="ol-icon">${icon('close')}</span></button>
    </div>
    <div class="ol-exercise__body"></div>
    <div class="ol-exercise__foot">
      <button type="button" class="ol-exercise__nav" data-nav="prev"></button>
      <button type="button" class="ol-exercise__reveal"></button>
      <button type="button" class="ol-exercise__nav" data-nav="next"></button>
    </div>`;
  o.root.appendChild(el);
  el.inert = true;

  const titleEl = /** @type {HTMLElement} */ (el.querySelector('.ol-exercise__title'));
  const body = /** @type {HTMLElement} */ (el.querySelector('.ol-exercise__body'));
  const prevBtn = /** @type {HTMLButtonElement} */ (el.querySelector('[data-nav="prev"]'));
  const nextBtn = /** @type {HTMLButtonElement} */ (el.querySelector('[data-nav="next"]'));
  const revealBtn = /** @type {HTMLButtonElement} */ (el.querySelector('.ol-exercise__reveal'));
  const closeBtn = /** @type {HTMLButtonElement} */ (el.querySelector('.ol-exercise__close'));

  /** @type {QuizSource | null} */
  let source = null;
  /** @type {QuizQuestion[] | null} */
  let questions = null;
  /** @type {'idle' | 'loading' | 'error' | 'ready'} */
  let state = 'idle';
  let index = 0;
  /** @type {(number | null)[]} */
  let selections = [];
  /** @type {boolean[]} */
  let revealed = [];

  /** Nạp câu hỏi từ `source` — CHỈ một lần cho tới khi setSource() cấp nguồn khác. */
  async function load() {
    if (!source) return;
    state = 'loading';
    render();
    try {
      const list = await source.fetchQuestions();
      questions = list;
      selections = list.map(() => null);
      revealed = list.map(() => false);
      index = 0;
      state = 'ready';
    } catch {
      state = 'error';
    }
    render();
  }

  /** @param {string} tag @param {string} text @param {string} [cls] */
  function h(tag, text, cls) {
    const e = document.createElement(tag);
    e.textContent = text;
    if (cls) e.className = cls;
    return e;
  }

  function render() {
    titleEl.textContent = o.t('header.exercise');
    const closeText = o.t('quiz.close');
    closeBtn.title = closeText;
    closeBtn.setAttribute('aria-label', closeText);

    body.innerHTML = '';
    prevBtn.textContent = `← ${o.t('quiz.prev')}`;
    nextBtn.textContent = `${o.t('quiz.next')} →`;
    prevBtn.disabled = true;
    nextBtn.disabled = true;
    revealBtn.disabled = true;
    revealBtn.innerHTML = `<span class="ol-icon">${icon('eye')}</span>${o.t('quiz.answer')}`;

    if (!source) {
      body.appendChild(h('p', o.t('quiz.empty'), 'ol-exercise__msg'));
      return;
    }
    if (state === 'loading') {
      body.appendChild(h('p', o.t('quiz.loading'), 'ol-exercise__msg'));
      return;
    }
    if (state === 'error') {
      body.appendChild(h('p', o.t('quiz.error'), 'ol-exercise__msg'));
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'ol-exercise__retry';
      retry.textContent = o.t('quiz.retry');
      retry.addEventListener('click', () => void load());
      body.appendChild(retry);
      return;
    }
    if (!questions || questions.length === 0) {
      body.appendChild(h('p', o.t('quiz.empty'), 'ol-exercise__msg'));
      return;
    }

    const q = /** @type {QuizQuestion} */ (questions[index]);
    body.appendChild(
      h(
        'p',
        o.t('quiz.progress', { n: index + 1, total: questions.length, section: q.section }),
        'ol-exercise__progress',
      ),
    );
    body.appendChild(h('h3', q.question, 'ol-exercise__question'));

    const list = document.createElement('div');
    list.className = 'ol-exercise__options';
    const sel = selections[index];
    const isRevealed = revealed[index];
    q.options.forEach((text, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ol-exercise__option';
      btn.classList.toggle('is-selected', sel === i);
      if (isRevealed) {
        if (i === q.correctIndex) btn.classList.add('is-correct');
        else if (sel === i) btn.classList.add('is-wrong');
      }
      btn.innerHTML = `<span class="ol-exercise__option-badge">${String.fromCharCode(65 + i)}</span><span>${escapeHtml(text)}</span>`;
      btn.addEventListener('click', () => {
        selections[index] = i;
        render();
      });
      list.appendChild(btn);
    });
    body.appendChild(list);

    prevBtn.disabled = index === 0;
    nextBtn.disabled = index === questions.length - 1;
    revealBtn.disabled = false;
    revealBtn.classList.toggle('is-on', isRevealed);
  }

  /** Tránh XSS nếu câu hỏi tới từ API thật sau này — text hiện là ctx.t() tin cậy được,
   * nhưng hợp đồng fetchQuestions() không cấm trả về text thô từ server. */
  /** @param {string} s */
  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  const emitOpen = () => o.onOpenChange?.(el.classList.contains('is-open'));
  const open = () => {
    el.inert = false;
    el.classList.add('is-open');
    if (state === 'idle' && source) void load();
    else render();
    emitOpen();
  };
  const close = () => {
    el.classList.remove('is-open');
    el.inert = true;
    emitOpen();
  };
  const toggle = () => (el.classList.contains('is-open') ? close() : open());

  const onPrevClick = () => {
    if (index > 0) {
      index -= 1;
      render();
    }
  };
  const onNextClick = () => {
    if (questions && index < questions.length - 1) {
      index += 1;
      render();
    }
  };
  const onRevealClick = () => {
    revealed[index] = !revealed[index];
    render();
  };
  const onCloseClick = () => close();
  prevBtn.addEventListener('click', onPrevClick);
  nextBtn.addEventListener('click', onNextClick);
  revealBtn.addEventListener('click', onRevealClick);
  closeBtn.addEventListener('click', onCloseClick);

  render();

  return {
    el,
    open,
    close,
    toggle,
    isOpen: () => el.classList.contains('is-open'),
    retranslate: render,
    /** @param {QuizSource | null} s Gọi từ ctx.ui.setQuiz() — null = tiết không có bài tập. */
    setSource(s) {
      source = s;
      questions = null;
      state = 'idle';
      if (el.classList.contains('is-open') && source) void load();
      else render();
    },
    dispose() {
      prevBtn.removeEventListener('click', onPrevClick);
      nextBtn.removeEventListener('click', onNextClick);
      revealBtn.removeEventListener('click', onRevealClick);
      closeBtn.removeEventListener('click', onCloseClick);
      el.remove();
    },
  };
}
